import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteConfirmationDialog from '../../components/common/DeleteConfirmationDialog';
import { toast } from '../../components/toast';
import { getUserGroups } from '../../utils';
import { HRPageHeader } from '../../components/hr';
import {
  createEmployeeShift,
  deleteEmployeeShift,
  getEmployeeShifts,
  updateEmployeeShift,
} from '../../services/api';

const allowedGroups = ['admin', 'hr'];

const getInitialFormData = () => ({
  name: '',
  shift_start_time: '',
  shift_end_time: '',
  grace_time_minutes: '0',
  half_day_threshold_minutes: '0',
  remarks: '',
});

const formatTime = (value) => (value ? value.slice(0, 5) : 'N/A');

export default function SettingsConfigurationPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [employeeShifts, setEmployeeShifts] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add');
  const [editingShift, setEditingShift] = useState(null);
  const [formData, setFormData] = useState(getInitialFormData());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [shiftToDelete, setShiftToDelete] = useState(null);

  const paginatedShifts = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return employeeShifts.slice(startIndex, startIndex + rowsPerPage);
  }, [employeeShifts, page, rowsPerPage]);

  const loadEmployeeShifts = async () => {
    setIsLoading(true);
    try {
      const response = await getEmployeeShifts(1, 1000);
      setEmployeeShifts(response?.data?.results || []);
    } catch (error) {
      console.error('Error loading employee shifts:', error);
      toast.error(error?.response?.data?.message || 'Failed to load employee shifts');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const userGroups = getUserGroups();
    if (userGroups === null || !allowedGroups.some((group) => userGroups.includes(group))) {
      navigate('/dashboard', { replace: true });
      return;
    }

    loadEmployeeShifts();
  }, [navigate]);

  const handleOpenDialog = (mode, shift = null) => {
    setDialogMode(mode);
    setEditingShift(shift);

    if (shift) {
      setFormData({
        name: shift.name || '',
        shift_start_time: shift.shift_start_time ? shift.shift_start_time.slice(0, 5) : '',
        shift_end_time: shift.shift_end_time ? shift.shift_end_time.slice(0, 5) : '',
        grace_time_minutes: String(shift.grace_time_minutes ?? 0),
        half_day_threshold_minutes: String(shift.half_day_threshold_minutes ?? 0),
        remarks: shift.remarks || '',
      });
    } else {
      setFormData(getInitialFormData());
    }

    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingShift(null);
    setFormData(getInitialFormData());
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    if (isSubmitting) {
      return;
    }

    if (!formData.name || !formData.shift_start_time || !formData.shift_end_time) {
      toast.error('Name, shift start time and shift end time are required');
      return;
    }

    const graceTimeMinutes = Number.parseInt(formData.grace_time_minutes, 10);
    if (Number.isNaN(graceTimeMinutes) || graceTimeMinutes < 0) {
      toast.error('Grace time minutes must be 0 or greater');
      return;
    }

    const halfDayThresholdMinutes = Number.parseInt(formData.half_day_threshold_minutes, 10);
    if (Number.isNaN(halfDayThresholdMinutes) || halfDayThresholdMinutes < 0) {
      toast.error('Half day threshold minutes must be 0 or greater');
      return;
    }

    const payload = {
      name: formData.name,
      shift_start_time: formData.shift_start_time,
      shift_end_time: formData.shift_end_time,
      grace_time_minutes: graceTimeMinutes,
      half_day_threshold_minutes: halfDayThresholdMinutes,
      remarks: formData.remarks || null,
    };

    setIsSubmitting(true);
    try {
      if (dialogMode === 'edit' && editingShift) {
        await updateEmployeeShift(editingShift.id, payload);
        toast.success('Employee shift updated successfully');
      } else {
        await createEmployeeShift(payload);
        toast.success('Employee shift created successfully');
      }

      handleCloseDialog();
      loadEmployeeShifts();
    } catch (error) {
      console.error('Error saving employee shift:', error);
      toast.error(error?.response?.data?.message || 'Failed to save employee shift');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteShift = (shift) => {
    setShiftToDelete(shift);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!shiftToDelete) {
      return;
    }

    try {
      await deleteEmployeeShift(shiftToDelete.id);
      toast.success('Employee shift deleted successfully');
      setOpenDeleteDialog(false);
      setShiftToDelete(null);
      loadEmployeeShifts();
    } catch (error) {
      console.error('Error deleting employee shift:', error);
      toast.error(error?.response?.data?.message || 'Failed to delete employee shift');
    }
  };

  const isViewMode = dialogMode === 'view';

  return (
    <>
      <Helmet>
        <title>BillerBay Transmit | HR Settings & Configuration</title>
      </Helmet>

      <HRPageHeader
        title="HR settings & configuration"
        description="Configure shared HR defaults such as employee shifts. Changes here affect scheduling and attendance across the module."
      />

      <Card>
        <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
            <Typography variant="h6">Employee Shifts</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog('add')}>
              Add Shift
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Manage employee shift settings.
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Start Time</TableCell>
                <TableCell>End Time</TableCell>
                <TableCell>Grace Time</TableCell>
                <TableCell>Half Day Threshold</TableCell>
                <TableCell>Remarks</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Loading shifts...
                  </TableCell>
                </TableRow>
              ) : paginatedShifts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No employee shifts found.
                  </TableCell>
                </TableRow>
              ) : (
                paginatedShifts.map((shift) => (
                  <TableRow key={shift.id} hover>
                    <TableCell>{shift.name}</TableCell>
                    <TableCell>{formatTime(shift.shift_start_time)}</TableCell>
                    <TableCell>{formatTime(shift.shift_end_time)}</TableCell>
                    <TableCell>{`${shift.grace_time_minutes ?? 0} min`}</TableCell>
                    <TableCell>{`${shift.half_day_threshold_minutes ?? 0} min`}</TableCell>
                    <TableCell>{shift.remarks || 'N/A'}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'inline-flex', gap: 0.5 }}>
                        <IconButton size="small" onClick={() => handleOpenDialog('view', shift)} sx={{ color: 'primary.main' }}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleOpenDialog('edit', shift)} sx={{ color: 'primary.main' }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeleteShift(shift)} sx={{ color: 'error.main' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={employeeShifts.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Card>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'add' ? 'Add Employee Shift' : dialogMode === 'edit' ? 'Edit Employee Shift' : 'View Employee Shift'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Shift Name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  disabled={isViewMode || isSubmitting}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Shift Start Time"
                  type="time"
                  value={formData.shift_start_time}
                  onChange={(e) => handleInputChange('shift_start_time', e.target.value)}
                  disabled={isViewMode || isSubmitting}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Shift End Time"
                  type="time"
                  value={formData.shift_end_time}
                  onChange={(e) => handleInputChange('shift_end_time', e.target.value)}
                  disabled={isViewMode || isSubmitting}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Grace Time (Minutes)"
                  type="number"
                  value={formData.grace_time_minutes}
                  onChange={(e) => handleInputChange('grace_time_minutes', e.target.value)}
                  disabled={isViewMode || isSubmitting}
                  inputProps={{ min: 0 }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Half Day Threshold (Minutes)"
                  type="number"
                  value={formData.half_day_threshold_minutes}
                  onChange={(e) => handleInputChange('half_day_threshold_minutes', e.target.value)}
                  disabled={isViewMode || isSubmitting}
                  inputProps={{ min: 0 }}
                  helperText="Minutes after shift start — employees checking in later than this are marked as Half Day (overrides Late)"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Remarks"
                  value={formData.remarks}
                  onChange={(e) => handleInputChange('remarks', e.target.value)}
                  disabled={isViewMode || isSubmitting}
                  multiline
                  minRows={3}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isSubmitting}>Cancel</Button>
          {!isViewMode && (
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {isSubmitting ? 'Saving...' : dialogMode === 'add' ? 'Create Shift' : 'Update Shift'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <DeleteConfirmationDialog
        open={openDeleteDialog}
        onClose={() => {
          setOpenDeleteDialog(false);
          setShiftToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Shift"
        message={`Are you sure you want to delete shift "${shiftToDelete?.name || ''}"?`}
      />
    </>
  );
}
